import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { config } from '../config';
import { apiClient } from '../lib/apiClient';

/**
 * Represents a table entry from the RAG data dictionary
 */
export interface DataDictionaryTable {
  id: string;
  tableName: string;
  content: string;
}

/**
 * Response from the RAG API documents endpoint
 */
interface DocumentResponse {
  id: string;
  collection_id: string;
  content: string | null;
  metadata: {
    table_name?: string;
    type?: string;
    filename?: string;
    owner_id?: string;
    name?: string;
  } | null;
  created_at: string | null;
  updated_at: string | null;
}

interface UseDataDictionaryResult {
  tables: DataDictionaryTable[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch the data dictionary tables from the RAG API
 */
export const useDataDictionary = (): UseDataDictionaryResult => {
  const fetchTables = useCallback(async (): Promise<DataDictionaryTable[]> => {
    const client = apiClient().extend({
      prefixUrl: config.RAG_API_URL,
    });

    const documents = await client
      .get(`collections/${config.RAG_TABLE_INFO_COLLECTION_ID}/documents`, {
        searchParams: { limit: '100' },
      })
      .json<DocumentResponse[]>();

    return documents
      .filter((doc) => doc.metadata?.table_name && doc.content)
      .map((doc) => ({
        id: doc.id,
        tableName: doc.metadata?.table_name ?? '',
        content: doc.content ?? '',
      }))
      .sort((a, b) => a.tableName.localeCompare(b.tableName));
  }, []);

  const {
    data: tables = [],
    isLoading,
    error,
    refetch,
  } = useQuery<DataDictionaryTable[], Error>({
    queryKey: ['dataDictionaryTables', config.RAG_API_URL, config.RAG_TABLE_INFO_COLLECTION_ID],
    queryFn: fetchTables,
  });

  const refetchTables = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  return {
    tables,
    isLoading,
    error,
    refetch: refetchTables,
  };
};
